import { useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, X, ChevronLeft } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import BookCover from "@/components/books/BookCover";
import PhysicalStickyBar from "@/components/books/PhysicalStickyBar";
import Reveal from "@/components/books/Reveal";
import BilingualBadge from "@/components/books/BilingualBadge";
import { HeroFade } from "@/components/books/KineticTitle";
import { CHINNU_BOOKS, CHINNU_BUNDLE, findChinnuBook, isReady, CHINNU_AUTHOR } from "@/data/books";

export default function ChinnuProduct() {
  const { bookSlug } = useParams();
  const book = useMemo(() => findChinnuBook(bookSlug), [bookSlug]);
  useTrackPageView("book_product_view", { product_id: book?.product_id, slug: bookSlug });
  useSEO({
    title: book ? `${book.title} (${book.title_telugu}) — Bilingual English–Telugu · Chinnu Talking Adventures · LifeIQ Books` : "Chinnu — LifeIQ Books",
    description: book && isReady(book.short_description) ? `Bilingual English–Telugu printed book, delivered across India. ${book.short_description}` : "A bilingual English–Telugu Chinnu Talking Adventures printed book from LifeIQ Books.",
    canonicalPath: `/books/chinnu/${bookSlug}`,
  });

  const [lightbox, setLightbox] = useState(null); // index or null

  if (!book) return <Navigate to="/books/chinnu" replace />;

  const related = CHINNU_BOOKS.filter((b) => b.slug !== book.slug);
  const previews = book.preview_assets || [];

  return (
    <main>
      {/* BREADCRUMB */}
      <div className="bg-[#FBF7EE] border-b border-[#E7DFCF]">
        <nav className="max-w-[1240px] mx-auto px-5 sm:px-8 py-4 flex items-center gap-2 text-[13px] text-[#5C6470] flex-wrap">
          <Link to="/books" className="hover:text-[#0D1B2A] transition-colors">LifeIQ Books</Link>
          <ChevronRight size={14} />
          <Link to="/books/chinnu" className="hover:text-[#0D1B2A] transition-colors">Chinnu Talking Adventures</Link>
          <ChevronRight size={14} />
          <span className="text-[#0D1B2A] font-medium">{book.title}</span>
        </nav>
      </div>

      {/* PRODUCT HERO — 3D tilt cover under a spotlight */}
      <section className="bg-[#0D1B2A] text-white relative overflow-hidden">
        <span
          aria-hidden="true"
          className="font-telugu absolute -bottom-10 right-0 text-[20vw] leading-none text-outline-cream opacity-40 pointer-events-none select-none"
        >
          {book.title_telugu}
        </span>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-16 md:py-24 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">
            <HeroFade delay={0.15} className="md:col-span-5">
              <div className="book-spotlight p-8 sm:p-12 max-w-[420px] mx-auto">
                <BookCover book={book} tilt shadow="book-shadow-3d" testId="book-cover-image" />
              </div>
            </HeroFade>

            <div className="md:col-span-7">
              <HeroFade delay={0.1}>
                <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">
                  Chinnu · Book {book.book_number} of {CHINNU_BOOKS.length}
                </div>
              </HeroFade>
              <HeroFade delay={0.22}>
                <h1 data-testid="book-title" className="mt-4 font-serif-d text-[38px] sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight text-white">
                  {book.title}
                </h1>
                <div className="mt-3 font-telugu text-[24px] sm:text-[28px] text-[#F5B25C] leading-[1.3]">{book.title_telugu}</div>
                <div data-testid="pdp-bilingual-strip" className="mt-4 flex flex-wrap items-center gap-2.5">
                  <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/80">Bilingual English–Telugu</span>
                  <BilingualBadge dark testId="pdp-bilingual-badge" />
                </div>
                {isReady(book.subtitle) && (
                  <div className="mt-3 font-serif-d italic text-[18px] sm:text-[20px] text-white/65">{book.subtitle}</div>
                )}
              </HeroFade>
              <HeroFade delay={0.34}>
                <p className="mt-6 text-[16px] sm:text-[17px] text-white/85 leading-[1.7] max-w-xl">{book.short_description}</p>
                <div className="mt-4 text-[15px] text-[#F5B25C] font-semibold">{book.key_benefit}</div>

                <div className="mt-8">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/55 font-semibold mb-3">Printed Edition</div>
                  <div data-testid="book-price" className="flex items-baseline gap-2">
                    <span className="text-[12px] uppercase tracking-[0.1em] text-white/50 font-semibold">Price</span>
                    <span className="font-serif-d text-[34px] text-white leading-none">{book.print_price}</span>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-start">
                  <div className="w-full">
                    <p data-testid="pdp-buy-context" className="mb-3 text-[12px] uppercase tracking-[0.14em] text-white/60 font-semibold">Bilingual English–Telugu · Printed Book</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link
                        to={`/books/chinnu/checkout/${book.slug}`}
                        data-testid="book-order-print-button"
                        onClick={() => track("chinnu_print_product_view", { product_id: book.print_product_id, source: "pdp-hero" })}
                        className="inline-flex items-center justify-center gap-2 text-[#0D1B2A] px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:opacity-90 transition-opacity duration-200 rounded-sm"
                        style={{ backgroundColor: "#F57F17", color: "#fff" }}
                      >
                        Order Printed Copy — {book.print_price}
                      </Link>
                      <a
                        href="#preview"
                        data-testid="pdp-look-inside"
                        onClick={() => track("look_inside_click", { product_id: book.product_id })}
                        className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                      >
                        Look Inside
                      </a>
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-[13px] text-white/55">Printed-book delivery is currently available within India only.</p>
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      {/* PRACTISE + INSIDE */}
      <section className="bg-[#FBF7EE]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            <Reveal>
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">What your child will practise</div>
              <h2 className="mt-4 font-serif-d text-[26px] sm:text-[32px] leading-[1.15] text-[#0D1B2A]">Everyday practice, on every page.</h2>
              <ul data-testid="practise-bullets-list" className="mt-8 space-y-3.5">
                {book.practise.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-[16px] text-[#1A1A1A] leading-[1.55]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F57F17] mt-[10px] shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">What's inside</div>
              <h2 className="mt-4 font-serif-d text-[26px] sm:text-[32px] leading-[1.15] text-[#0D1B2A]">A quick look at the contents.</h2>
              <ul data-testid="inside-bullets-list" className="mt-8 space-y-3.5">
                {book.inside.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-[16px] text-[#1A1A1A] leading-[1.55]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#004D40] mt-[10px] shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LOOK INSIDE — preview gallery + lightbox */}
      <section id="preview" className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Preview</div>
              <h2 data-testid="look-inside-section" className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1] text-[#0D1B2A]">Look inside {book.title}.</h2>
              <p className="mt-4 text-[15px] text-[#5C6470] leading-[1.7]">Sample pages straight from the printed book. Order your copy to read every page at home.</p>
            </div>
          </Reveal>

          {previews.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="mt-10 rounded-sm border border-dashed border-[#D7DADD] bg-[#FBF7EE] p-10 sm:p-14 text-center">
                <div className="font-telugu text-[30px] text-[#F57F17]">త్వరలో</div>
                <div className="mt-2 text-[12px] uppercase tracking-[0.22em] text-[#5C6470] font-semibold">Preview pages</div>
                <div className="mt-3 font-serif-d text-[22px] text-[#0D1B2A]">Sample pages are being prepared</div>
                <p className="mt-2 text-[14px] text-[#5C6470] max-w-md mx-auto">Real pages from this book will appear here shortly — check back soon.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {previews.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`preview-thumbnail-${i}`}
                    onClick={() => setLightbox(i)}
                    className="aspect-[3/4] bg-[#FBF7EE] rounded-sm overflow-hidden border border-[#E7DFCF] hover:border-[#F57F17] hover:shadow-[0_10px_30px_rgba(13,27,42,0.12)] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#F57F17]"
                  >
                    <img src={src} alt={`${book.title} preview page ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </Reveal>
          )}
        </div>

        {/* LIGHTBOX */}
        <AnimatePresence>
          {lightbox !== null && previews[lightbox] && (
            <motion.div
              data-testid="preview-lightbox"
              className="fixed inset-0 z-[90] bg-[#0D1B2A]/95 flex items-center justify-center p-4 sm:p-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setLightbox(null)}
            >
              <button type="button" aria-label="Close preview" data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white p-2" onClick={() => setLightbox(null)}>
                <X size={26} />
              </button>
              {lightbox > 0 && (
                <button type="button" aria-label="Previous page" data-testid="lightbox-prev" className="absolute left-3 sm:left-8 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}>
                  <ChevronLeft size={32} />
                </button>
              )}
              <motion.img
                key={lightbox}
                src={previews[lightbox]}
                alt={`${book.title} preview page ${lightbox + 1}`}
                className="max-h-[85vh] max-w-full rounded-sm shadow-2xl"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => e.stopPropagation()}
              />
              {lightbox < previews.length - 1 && (
                <button type="button" aria-label="Next page" data-testid="lightbox-next" className="absolute right-3 sm:right-8 text-white/80 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}>
                  <ChevronRight size={32} />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* CHAPTER MAP — real contents from the book */}
      {book.chapters && book.chapters.length > 0 && (
        <section className="bg-[#F5F2EA]">
          <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">The chapter map</div>
                  <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1] text-[#0D1B2A]">
                    Every chapter, straight from the book.
                  </h2>
                </div>
                <div className="text-[12px] uppercase tracking-[0.18em] text-[#5C6470] font-semibold">
                  {book.chapters.length} chapters · {book.pages} pages
                </div>
              </div>
            </Reveal>
            <div data-testid="chapter-map" className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-0">
              {book.chapters.map((c, i) => (
                <Reveal key={c.name} delay={(i % 3) * 0.06}>
                  <div data-testid={`chapter-${i + 1}`} className="flex gap-4 py-4 border-b border-[#E7DFCF]">
                    <span className="font-serif-d text-[18px] text-[#F57F17] leading-snug w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="font-serif-d text-[17px] text-[#0D1B2A] leading-snug">{c.name}</div>
                      <div className="mt-0.5 text-[13px] text-[#5C6470]">{c.desc}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PARENT NOTE */}
      <section className="bg-[#FBF7EE] relative overflow-hidden">
        <span aria-hidden="true" className="font-telugu absolute -top-6 right-6 text-[10rem] leading-none text-[#F57F17]/10 pointer-events-none select-none">{book.title_telugu}</span>
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-20 md:py-24 relative">
          <Reveal>
            <div data-testid="parent-note-box">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">A note for parents</div>
              <p className="mt-6 font-serif-d italic text-[22px] sm:text-[28px] text-[#0D1B2A] leading-[1.35] border-l-[3px] border-[#F57F17] pl-6">
                {book.parent_note}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRINTED EDITION FACTS */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-16 md:py-20">
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
              {[
                ["Languages", book.languages],
                ["Format", `Printed Book · ${book.pages} pages`],
                ["Binding", "Full-colour, child-friendly paperback"],
                ["Delivery", "Shipped across India after payment"],
                ["Use", "Personal / family or gifting"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C6470] font-semibold">{k}</div>
                  <div className="mt-2 font-serif-d text-[17px] text-[#0D1B2A] leading-snug">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-[#E5E7EA] flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <img
                src={CHINNU_AUTHOR.photo}
                alt={CHINNU_AUTHOR.photo_alt}
                data-testid="pdp-author-photo"
                className="w-12 h-12 rounded-full object-cover object-top shrink-0 border border-[#E7DFCF]"
              />
              <div className="text-[12px] text-[#5C6470] tracking-wide">
                Written & designed by <span className="font-semibold text-[#0D1B2A]">{CHINNU_AUTHOR.name}</span> · First Edition, 2026 · © 2026 LifeIQ EdTech LLP · LifeIQ Publications, Hyderabad
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECONDARY BUY */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-16 md:py-20 text-center">
          <Reveal>
            <h2 className="font-serif-d text-[28px] sm:text-4xl leading-[1.15] text-white max-w-2xl mx-auto">Ready when you are.</h2>
            <p className="mt-4 text-[15px] sm:text-[16px] text-white/75 max-w-xl mx-auto">A short book, warmly written. Order your printed copy and read the first scene together once it arrives.</p>
            <div className="mt-8 inline-flex flex-col items-center">
              <div className="flex items-baseline gap-2.5 justify-center mb-4">
                <span className="font-serif-d text-[34px] text-white leading-none">{book.print_price}</span>
              </div>
              <p data-testid="pdp-bottom-buy-context" className="mb-3 text-[12px] uppercase tracking-[0.14em] text-white/55 font-semibold">Bilingual English–Telugu · Printed Book</p>
              <Link
                to={`/books/chinnu/checkout/${book.slug}`}
                data-testid="pdp-buy-bottom"
                onClick={() => track("chinnu_print_product_view", { product_id: book.print_product_id, source: "pdp-bottom" })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-[13px] font-semibold tracking-wide uppercase text-white hover:opacity-90 transition-opacity duration-200 rounded-sm"
                style={{ backgroundColor: "#F57F17" }}
              >
                Order Printed Copy — {book.print_price}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED BOOKS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">More Chinnu</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1] text-[#0D1B2A]">Continue the journey.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            {related.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <Link
                  to={`/books/chinnu/${b.slug}`}
                  data-testid={`related-book-${b.slug}`}
                  onClick={() => track("book_product_view", { product_id: b.product_id, source: "related" })}
                  className="group flex gap-5 bg-[#FBF7EE] border border-[#E7DFCF] rounded-sm p-5 hover:border-[#F57F17]/60 hover:shadow-[0_8px_30px_rgba(13,27,42,0.08)] transition-all duration-200"
                >
                  <div className="w-20 shrink-0"><BookCover book={b} testId={`related-book-card-${b.slug}`} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Book {b.book_number}</div>
                    <div className="mt-1 font-serif-d text-[19px] text-[#0D1B2A] leading-tight">{b.title}</div>
                    <div className="mt-0.5 font-telugu text-[14px] text-[#D86B0F]">{b.title_telugu}</div>
                    <div className="mt-2 text-[13px] text-[#5C6470] font-semibold">{b.print_price} · Printed Book</div>
                    <div className="mt-2 text-[12px] uppercase tracking-[0.14em] font-semibold text-[#004D40] group-hover:underline">View book →</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE CROSS-SELL */}
      <section className="bg-[#FBF7EE]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-16 md:py-20 text-center">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Save with the bundle</div>
            <h2 className="mt-4 font-serif-d text-[26px] sm:text-4xl leading-[1.15] text-[#0D1B2A] max-w-2xl mx-auto">Prefer the whole collection?</h2>
            <p className="mt-3 text-[15px] text-[#5C6470] max-w-xl mx-auto">
              All four Chinnu adventures, printed, for {CHINNU_BUNDLE.print_price} — save {CHINNU_BUNDLE.print_savings} over buying separately.
            </p>
            <Link
              to="/books/chinnu/complete-collection"
              data-testid="bundle-crosssell-link"
              onClick={() => track("bundle_view", { source: "pdp" })}
              className="mt-6 inline-flex items-center gap-2 border border-[#0D1B2A] text-[#0D1B2A] px-6 py-3.5 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:bg-[#0D1B2A] hover:text-white transition-colors duration-200"
            >
              See the Complete Collection <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MOBILE STICKY BUY BAR */}
      <PhysicalStickyBar
        href={`/books/chinnu/checkout/${book.slug}`}
        price={book.print_price}
        label="Order Printed Copy"
        testId="pdp-sticky-buy"
      />
    </main>
  );
}
